import { ReactNode, useEffect, useRef } from "react";
import cx from "classnames";

import { UISubscriptionStatus } from "common/types";
import { toPhone } from "common/utils/formatting";
import { parseNumber } from "common/utils/numbers";
import { SubscriptionPriceSummary } from "common/types/priceSummary";
import { useMatch, useSearchState } from "common/router/hooks";
import { Optional } from "common/utils/types";
import { stringToValidValue } from "common/utils/strings";

import Page from "client/common/components/layouts/Page";
import Subheader from "client/common/components/layouts/Subheader";
import Body from "client/common/components/layouts/Body";
import PillButton from "client/common/components/buttons/PillButton";
import Address from "client/common/components/content/Address";
import PaymentMethod from "client/common/components/content/PaymentMethod";
import { ModalStatus } from "client/common/types";
import { useClientQuery } from "client/accounts/hooks";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";
import LoadingPage from "client/common/components/loading/LoadingPage";
import Banner from "client/common/components/content/Banner";
import { useClinic, useFeatures } from "client/clinics/hooks";
import { useBreakpoints } from "client/common/hooks";
import Button from "client/common/components/buttons/Button";
import { useCartItemAddMutation } from "client/carts/hooks";
import { CartCode } from "client/carts/types";
import { IClient } from "client/accounts/types";
import PriceSummary from "client/common/components/display/PriceSummary";
import EmployeePricingNote from "client/common/components/content/EmployeePricingNote";
import { Paths, urls } from "client/routes";
import {
  sendAddToCart,
  getAddToCartAutoshipFields,
  useSendPostHogAddToCart,
} from "client/analytics/utils";
import { AddToCartPageSource } from "client/analytics/types";

import { useSubscriptionQuery } from "../hooks";
import { DetailSubscription, SubscriptionForm } from "../types";
import SubscriptionStatus from "./SubscriptionStatus";
import SubscriptionPaymentWarningIcon from "./SubscriptionPaymentWarningIcon";
import SubscriptionAddressWarningIcon from "./SubscriptionAddressWarningIcon";
import SubscriptionIssueBanner from "./SubscriptionIssueBanner";
import SubscriptionItem from "./SubscriptionItem";
import SubscriptionActions from "./SubscriptionActions";
import {
  getSubscriptionSavingsBanner,
  getSubscriptionUserActions,
  shouldShowSubscriptionClientInfoAlert,
  shouldShowSubscriptionIssueBanner,
} from "../utils";
import {
  useSubscriptionAddressUpdateDrawer,
  useSubscriptionCancelDialog,
  useSubscriptionResumeDrawer,
  useSubscriptionSkipNextOrderDialog,
  useSubscriptionFrequencyUpdateDrawer,
  useSubscriptionPaymentUpdateDrawer,
  useSubscriptionQuantityUpdateDrawer,
  useSubscriptionNextOrderDateUpdateDrawer,
} from "../stackables";

import styles from "../styles/SubscriptionPage.module.less";

const noop = () => undefined;

type SubscriptionProps = {
  subscription: DetailSubscription;
  client: IClient;
};

type SubscriptionSummaryInfo = {
  label: string;
  value: ReactNode;
  /** Rendered beside the label to flag a problem with the value below it. */
  marker?: ReactNode;
  action?: ReactNode;
  className?: string;
};

type SubscriptionDetailFormOpts = {
  subscription: DetailSubscription;
  form: Optional<SubscriptionForm>;
  isOpen: boolean;
  onClose: () => void;
};

const useSubscriptionDetailForm = ({
  subscription,
  form,
  isOpen,
  onClose,
}: SubscriptionDetailFormOpts) => {
  const { open: openFrequencyUpdate } = useSubscriptionFrequencyUpdateDrawer();
  const { open: openQuantityUpdate } = useSubscriptionQuantityUpdateDrawer();
  const { open: openAddressUpdate } = useSubscriptionAddressUpdateDrawer();
  const { open: openSubscriptionResume } = useSubscriptionResumeDrawer();
  const { open: openSubscriptionPaymentUpdate } = useSubscriptionPaymentUpdateDrawer();
  const { open: openSubscriptionNextOrderDateUpdate } = useSubscriptionNextOrderDateUpdateDrawer();
  const { open: openSubscriptionCancel } = useSubscriptionCancelDialog();
  const { open: openSubscriptionSkipNextOrder } = useSubscriptionSkipNextOrderDialog();
  /**
   * Track if we have opened the form previously to prevent opening the same form twice locally
   * because react strict mode will call the useEffect twice.
   */
  const openedFormRef = useRef<SubscriptionForm | null>(null);

  const handleClose = () => {
    openedFormRef.current = null;
    onClose();
  };

  useEffect(() => {
    if (form && isOpen) {
      if (openedFormRef.current === form) {
        return;
      }
      openedFormRef.current = form;
      const formToOpen: Record<SubscriptionForm | "", () => void> = {
        updateQuantity: () => openQuantityUpdate({ id: subscription.id, onClose: handleClose }),
        updateFrequency: () => openFrequencyUpdate({ id: subscription.id, onClose: handleClose }),
        updateAddress: () => openAddressUpdate({ id: subscription.id, onClose: handleClose }),
        updatePayment: () =>
          openSubscriptionPaymentUpdate({ id: subscription.id, onClose: handleClose }),
        updateNextOrder: () =>
          openSubscriptionNextOrderDateUpdate({ id: subscription.id, onClose: handleClose }),
        skipNextOrder: () => openSubscriptionSkipNextOrder({ subscription, onClose: handleClose }),
        cancel: () => openSubscriptionCancel({ subscription, onClose: handleClose }),
        resume: () => openSubscriptionResume({ id: subscription.id, onClose: handleClose }),
        "": noop,
      };
      const open = formToOpen[form ?? ""];
      open?.();
    }
    // we only want to trigger opening the form when the param changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isOpen]);
};

const Subscription = (props: SubscriptionProps) => {
  const { client, subscription } = props;

  const clinic = useClinic();
  const { isPhoneAndSmaller } = useBreakpoints();
  const sendPostHogAddToCart = useSendPostHogAddToCart();

  const [search, setSearch] = useSearchState<Paths["subscriptions"]["detail"]>({
    form: undefined,
    visible: ModalStatus.Closed,
  });

  useSubscriptionDetailForm({
    subscription,
    form: stringToValidValue(search.form ?? "", Object.values(SubscriptionForm), null),
    isOpen: search.visible === ModalStatus.Open,
    onClose: () => setSearch({ ...search, visible: ModalStatus.Closed }),
  });

  // We may want to add a toast on error here.
  const { mutate: addCartItem, isPending: isAddingToCart } = useCartItemAddMutation({
    shouldOpenCart: true,
    onSuccess: (cartItemId, quantity, body, cartItemSubscription) => {
      // TODO: price here is the order total, not per-unit — consider using
      // next_order_data.unit_price in a future iteration.
      // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart
      const price = subscription.next_order_data.subtotal - subscription.next_order_data.discount;
      sendAddToCart({
        item: subscription.item,
        quantity,
        price,
        discount: subscription.next_order_data.discount,
      });
      sendPostHogAddToCart({
        source: AddToCartPageSource.Subscription,
        cart_id: typeof body.cart_id === "number" ? body.cart_id : null,
        cart_item_id: cartItemId,
        item_id: body.item,
        product_id: subscription.item.product.id,
        product_line_id: subscription.item.product.product_line.id,
        patient_id: body.patient_id ?? null,
        flavor_selection_id: body.flavor_selection ?? null,
        quantity,
        unit_price: price,
        discount: subscription.next_order_data.discount,
        ...getAddToCartAutoshipFields(cartItemSubscription),
      });
    },
  });

  const addToCart = () => {
    addCartItem({
      item: subscription.item.id,
      quantity: 1,
      cart_id: CartCode.ACTIVE,
      patient_id: subscription.patient.id,
    });
  };

  const isSubscriptionActive = subscription.visible_status === UISubscriptionStatus.Active;
  // No shipping quote is itself the warning ("unavailable for shipping"), and the
  // chip tooltip only exists for prescription items. Keep that warning in the banner slot.
  const showSubscriptionIssueBanner =
    shouldShowSubscriptionIssueBanner(subscription) &&
    subscription.next_order_data.shipping !== null;

  const features = useFeatures();
  const isEdbEnabled = features.AUTOSHIP_EDB;
  const showSubscriptionClientInfoBanner =
    shouldShowSubscriptionClientInfoAlert(
      subscription,
      features.CONTROLLED_SUBSTANCE_GRANULAR_CONTROLS,
    ) && subscription.next_order_data.shipping !== null;
  const isBannerSlotTakenByIssue = showSubscriptionIssueBanner || showSubscriptionClientInfoBanner;
  const userActions = getSubscriptionUserActions(subscription, { isEdbEnabled });
  const savingsBanner = getSubscriptionSavingsBanner(subscription, {
    clinic,
    isAutoshipSavingsShown: client.recurring_subscription_eligible_for_clinic,
    isEdbEnabled,
  });

  const summary: SubscriptionSummaryInfo[] = [
    {
      label: "Shipping address",
      marker: <SubscriptionAddressWarningIcon subscription={subscription} />,
      value: subscription.shipping_address ? (
        <Address address={subscription.shipping_address} />
      ) : (
        " No address configured"
      ),
      action: (
        <PillButton
          onClick={() => setSearch({ form: "updateAddress", visible: ModalStatus.Open })}
          size="s"
          variant="secondary"
        >
          Edit<span className={styles.long}>&nbsp;Shipping Address</span>
        </PillButton>
      ),
      className: styles.block,
    },
    {
      label: "Shipping updates",
      value: (
        <div className={styles.content}>
          {client.primary_contact_email?.email && <div>{client.primary_contact_email.email}</div>}
          {client.primary_contact_number?.number && (
            <div>{toPhone(client.primary_contact_number.number)}</div>
          )}
        </div>
      ),
      action: (
        <PillButton
          dest={urls.accounts.detail({
            search: { form: "updateAccount", visible: ModalStatus.Open },
          })}
          variant="secondary"
          size="s"
        >
          Edit<span className={styles.long}>&nbsp;Contact Info</span>
        </PillButton>
      ),
      className: styles.block,
    },
    {
      label: "Paid with",
      marker: <SubscriptionPaymentWarningIcon subscription={subscription} />,
      value: subscription.payment_method ? (
        <PaymentMethod method={subscription.payment_method} />
      ) : (
        "No payment method configured"
      ),
      action: (
        <PillButton
          variant="secondary"
          onClick={() => setSearch({ form: "updatePayment", visible: ModalStatus.Open })}
          size="s"
        >
          Edit<span className={styles.long}>&nbsp;Payment Info</span>
        </PillButton>
      ),
      className: styles.block,
    },
    {
      label: "Summary",
      value: (
        <>
          <PriceSummary.ExternalTax<SubscriptionPriceSummary>
            data={subscription.next_order_data}
            tax={null}
            isTaxFinal={false}
            styles={{
              rows: styles.rows,
              row: styles.row,
              subrows: styles.subrows,
              total: styles.total,
            }}
          />
          {subscription.should_show_employee_pricing_note && <EmployeePricingNote />}
        </>
      ),
      className: styles.totals,
    },
  ];

  const renderActions = () => {
    return userActions.map((action) => (
      <PillButton
        key={action.action}
        dest={action.dest}
        variant={action.isPrimary ? "primary" : "secondary"}
        size={isPhoneAndSmaller ? "s" : "m"}
      >
        {action.label}
      </PillButton>
    ));
  };

  const renderBanner = () => {
    // A problem the client can resolve takes the slot ahead of the prescription warning,
    // which falls back to a tooltip on the item's chip.
    if (showSubscriptionIssueBanner) {
      return <SubscriptionIssueBanner subscription={subscription} className={styles.banner} />;
    }

    if (showSubscriptionClientInfoBanner) {
      return (
        <SubscriptionIssueBanner.ClientInfo subscription={subscription} className={styles.banner} />
      );
    }

    if (subscription.prescription_warning) {
      return (
        <Banner variant="warning" inline={true} className={styles.banner}>
          <Banner.Text label={subscription.prescription_warning} />
        </Banner>
      );
    }

    if (!savingsBanner) {
      return null;
    }

    return (
      <Banner variant="success" inline={true} className={styles.banner}>
        {savingsBanner.description ? (
          <Banner.Content label={savingsBanner.label} description={savingsBanner.description} />
        ) : (
          <Banner.Text label={savingsBanner.label} />
        )}
      </Banner>
    );
  };

  return (
    <Page>
      <Subheader
        title={`Autoship for ${subscription.patient.name_display}`}
        renderStatus={() => <SubscriptionStatus subscription={subscription} />}
        renderActions={renderActions}
      />
      <Body>
        <div className={styles.container}>
          <div className={styles.main}>
            <div className={styles.item}>
              {renderBanner()}
              <div className={styles.details}>
                <SubscriptionItem
                  subscription={subscription}
                  prescriptionWarningTooltip={
                    isBannerSlotTakenByIssue ? (subscription.prescription_warning ?? null) : null
                  }
                />
              </div>
            </div>
            <div className={styles.modify}>
              <SubscriptionActions
                subscription={subscription}
                classNames={{ actions: styles.actions, action: styles.action }}
              />
            </div>
          </div>
          <div className={styles.summary}>
            {summary.map((info) => (
              <div className={cx(styles.info, info.className)} key={info.label}>
                <div className={styles.primary}>
                  <div className={styles.label}>
                    {info.label}
                    {info.marker}
                  </div>
                  <div className={styles.value}>{info.value}</div>
                </div>
                <div className={styles.action}>{info.action}</div>
              </div>
            ))}
          </div>
          {isPhoneAndSmaller && !isSubscriptionActive && (
            <div className={styles.mobileActions}>
              <Button isLoading={isAddingToCart} onClick={addToCart}>
                Reorder Item
              </Button>
              {userActions.map((action) => (
                <Button key={action.action} dest={action.dest} variant="secondary">
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </Body>
    </Page>
  );
};

const SubscriptionPage = () => {
  const match = useMatch<Paths["subscriptions"]["detail"]>();
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();
  const {
    data: subscription,
    isLoading: isSubscriptionLoading,
    error: subscriptionError,
  } = useSubscriptionQuery(parseNumber(match.params.id, 0));

  if (isClientLoading || isSubscriptionLoading) {
    return <LoadingPage />;
  }

  if (!client || clientError) {
    return <FetchErrorPage entityName="account" error={clientError} />;
  }

  if (!subscription || subscriptionError) {
    return <FetchErrorPage entityName="Autoship" error={subscriptionError} />;
  }

  return <Subscription subscription={subscription} client={client} />;
};

export default SubscriptionPage;
